import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Inbox, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import AdminNavbar from './AdminNavbar';
import { SubmissionCard } from './inbox/SubmissionCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = ['all', 'new', 'read', 'replied', 'archived'];

const ContactInbox = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/contact/submissions`, { withCredentials: true });
        if (active) setSubmissions(response.data || []);
      } catch (err) {
        if (!active) return;
        if (err.response?.status === 403) setAccessDenied(true);
        else toast.error('Failed to load messages');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [authLoading, isAuthenticated]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/contact/submissions`, { withCredentials: true });
      setSubmissions(response.data || []);
    } catch (err) {
      toast.error('Failed to refresh messages');
    }
  };

  const handleToggle = async (submission) => {
    const opening = expandedId !== submission.submission_id;
    setExpandedId(opening ? submission.submission_id : null);
    // Auto-mark a 'new' message as read when opened
    if (opening && submission.status === 'new') {
      await handleStatusChange(submission.submission_id, 'read', { silent: true });
    }
  };

  const handleStatusChange = async (submissionId, status, { silent = false } = {}) => {
    // Optimistic update
    setSubmissions((prev) => prev.map((s) =>
      s.submission_id === submissionId ? { ...s, status } : s
    ));
    try {
      await axios.put(`${API}/contact/submissions/${submissionId}/status`, { status }, { withCredentials: true });
      if (!silent) toast.success(`Marked as ${status}`);
    } catch (err) {
      if (!silent) toast.error('Failed to update status');
      fetchSubmissions(); // revert on failure
    }
  };

  const filtered = filter === 'all'
    ? submissions
    : submissions.filter((s) => s.status === filter || s.submission_id === expandedId);
  const newCount = submissions.filter((s) => s.status === 'new').length;

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <AdminNavbar />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin" />
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <AdminNavbar showInboxLink={false} />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-[#7dd3fc] font-bold text-2xl mb-2">Access Denied</h2>
            <p className="text-[#888680] mb-6">You need super admin privileges to view the inbox.</p>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              className="bg-[#7dd3fc] text-[#1a1c1b] rounded-full px-6 py-3"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <AdminNavbar />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 bg-gradient-to-br from-[#7dd3fc] to-[#38bdf8] rounded-xl flex items-center justify-center">
            <Inbox className="w-5 h-5 text-[#1a1c1b]" />
          </div>
          <div>
            <h1 className="text-[#7dd3fc] font-bold text-2xl md:text-3xl">Contact Inbox</h1>
            <p className="text-[#888680] text-sm">
              {newCount > 0 ? `${newCount} new message${newCount > 1 ? 's' : ''}` : 'All caught up'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 my-6">
          {FILTERS.map((f) => {
            const count = f === 'all' ? submissions.length : submissions.filter((s) => s.status === f).length;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`inbox-filter-${f}`}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all ${
                  filter === f
                    ? 'bg-[#7dd3fc] text-[#1a1c1b]'
                    : 'bg-[#302f2c] text-[#888680] hover:text-[#7dd3fc]'
                }`}
              >
                {f} {count > 0 && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <Inbox className="w-16 h-16 text-[#1e3a5f] mx-auto mb-4" />
            <h3 className="text-[#7dd3fc] font-bold text-xl mb-2">No messages</h3>
            <p className="text-[#888680]">
              {filter === 'all' ? 'Contact form submissions will appear here.' : `No ${filter} messages.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((submission) => (
              <SubmissionCard
                key={submission.submission_id}
                submission={submission}
                isExpanded={expandedId === submission.submission_id}
                onToggle={() => handleToggle(submission)}
                onStatusChange={(id, status) => handleStatusChange(id, status)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ContactInbox;
